/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:03]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DropDown,
    Form,
    ImageUpload,
    Input,
    Modal,
    Pending,
    requiredRule,
    toInt,
    useErrorState,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse } from 'axios';
import uploadFile from 'axios/fileUploadApi';
import { getLeadgenNamesDropDown } from 'axios/leadgenApi';
import { getRejectedTrafficRules } from 'axios/rejectedTrafficApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { RejectedTrafficAdd, RejectedTrafficRuleType } from 'models/api/rejectedTraffic';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Container, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useRejectedTrafficWorkplaceState } from './RejectedTraffic';

type RejectedTrafficRuleExtendedType = RejectedTrafficRuleType & {
    isLocked?: boolean;
};

type RejectedTrafficRuleForm = Omit<RejectedTrafficRuleExtendedType, 'id'> & { id?: number };

type RejectedTrafficForm = {
    leadgenId: number;
    isActive: boolean;
    logoFileId: number;
    referralLink: string;
    rule: number;
};

const initialItem: RejectedTrafficForm = {
    leadgenId: 0,
    logoFileId: 0,
    isActive: false,
    referralLink: '',
    rule: 0,
};

type RejectedTrafficModalProps = {
    isOpen: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const RejectedTrafficModal = ({ isOpen = false, toggle }: RejectedTrafficModalProps) => {
    const { hasRole } = useAuth();
    const api = useApi();
    const className = 'rejected-traffic-modal';
    const { handleRequestError, handleError } = useErrorState();
    const newData = useRef<RejectedTrafficForm>();

    const { t } = useTranslation();
    const { currentItem, changeItemMutation, addItemMutation } = useRejectedTrafficWorkplaceState();
    const [file, setFile] = useState<File>();
    const [doImageUpload, setDoImageUpload] = useState<boolean>(false);

    const [rejectedTrafficRules, setRejectedTrafficRules] = useState<RejectedTrafficRuleForm[]>([]);
    const [nameList, setNameList] = useState<DropDown[]>([]);
    const [isImageValid, setIsImageValid] = useState(true);

    const [rejectedTraffic, setRejectedTraffic] = useState<RejectedTrafficForm>(
        currentItem || initialItem
    );

    useEffect(
        () =>
            setRejectedTraffic({
                ...initialItem,
                ...currentItem,
                leadgenId: currentItem
                    ? currentItem.leadgenId
                    : nameList.length
                      ? toInt(nameList[0].id) || 0
                      : 0,
            }),
        [currentItem, nameList]
    );

    const { isFetching: isFetchingRules } = useQuery(
        'rejectedTrafficRulesList',
        () => getRejectedTrafficRules(api),
        {
            onSuccess: (data) =>
                setRejectedTrafficRules(
                    !currentItem
                        ? [
                              {
                                  rule: 0,
                                  isLocked: false,
                              },
                              ...data,
                          ]
                        : data
                ),
            enabled: !rejectedTrafficRules.length,
            onError: (e) => handleRequestError(e),
        }
    );

    const { isFetching } = useQuery('leadgenNameList', () => getLeadgenNamesDropDown(api), {
        onSuccess: (data) => {
            const list = data.map((x) => ({ ...x, id: toInt(x.id) || null }));
            setNameList(list);
        },
        enabled: !nameList.length,
    });

    const saveChanges = async (data: RejectedTrafficForm) => {
        const formattedItem: RejectedTrafficAdd = {
            leadgenId: toInt(data.leadgenId) || 0,
            isActive: data.isActive,
            logoFileId: data.logoFileId,
            referralLink: data.referralLink,
            rule: rejectedTrafficRules.find((x) => x.id === currentItem?.id)?.rule,
            rules: rejectedTrafficRules.reduce(
                (res, val) => {
                    if (val.id !== undefined) {
                        const ind = parseInt(val.id.toString());
                        res[ind] = val.rule;
                    }

                    return res;
                },
                {} as { [id: number]: number }
            ),
        };

        currentItem
            ? changeItemMutation.mutate({ ...formattedItem, id: currentItem.id })
            : addItemMutation.mutate({ ...formattedItem });

        closeModal();
    };

    const toggleLock = (id: string | undefined) => {
        const newRules = rejectedTrafficRules.map((x) => ({
            ...x,
            isLocked: `${x.id}` === `${id}` ? !x.isLocked : x.isLocked,
        }));
        setRejectedTrafficRules([...newRules]);
    };

    const handleRuleChange = (id: string | undefined, rule: number): boolean => {
        const lockedRulesSum = rejectedTrafficRules
            .filter((x) => x.isLocked)
            .reduce((acc, next) => acc + next.rule, 0);
        const unLockedRules = rejectedTrafficRules.filter((x) => !x.isLocked);
        const excessPercent = Math.floor(
            (100 - rule - lockedRulesSum) / (unLockedRules.length - 1)
        );
        if (excessPercent < 0 || excessPercent > 100) {
            return false;
        }
        let remainder = 100 - rule - lockedRulesSum - excessPercent * (unLockedRules.length - 1);

        const newRules = rejectedTrafficRules.map((x) => ({
            ...x,
            rule:
                `${x.id}` === `${id}`
                    ? rule
                    : !x.isLocked
                      ? excessPercent + (remainder > 0 ? remainder-- : 0)
                      : x.rule,
        }));
        setRejectedTrafficRules([...newRules]);
        return true;
    };

    const closeModal = () => {
        toggle(false);
    };

    const rules = rejectedTrafficRules.map((rt, idx) => {
        return (
            <Input
                key={idx}
                isFloating={false}
                type="range"
                value={rt.rule}
                disabled={rt.isLocked}
                onChange={(e) => {
                    const oldValue = e.target.defaultValue;

                    if (!handleRuleChange(rt.id?.toString(), parseInt(e.target.value))) {
                        e.target.value = oldValue;
                    }
                }}
                label={
                    <>
                        {`${t('BackOfficeRejectedTrafficRule')} (${
                            rt.id === currentItem?.id ? t('BackOfficeCurrent') : rt.leadgenName
                        }): ${rt.rule}%`}
                        <FontAwesomeIcon
                            role={'button'}
                            className={`${rt.isLocked ? 'text-primary' : ''} ms-1`}
                            icon={faLock}
                            onClick={() => toggleLock(rt.id?.toString())}
                        />
                    </>
                }
            />
        );
    });

    const formHasRole = (): boolean =>
        currentItem ? hasRole('rejectedTrafficUpdate') : hasRole('rejectedTrafficCreate');

    return (
        <Modal
            size="lg"
            className={className}
            isOpen={isOpen}
            toggle={closeModal}
            scrollable
            data-testid="rejected-traffic-modal"
        >
            <Form<RejectedTrafficForm>
                onSubmit={(data) => {
                    setRejectedTraffic(data);
                    if (file) {
                        newData.current = data;
                        setDoImageUpload(true);
                    } else if (currentItem) {
                        saveChanges(data);
                    } else {
                        setIsImageValid(false);
                    }
                }}
                onInvalid={() => {
                    if (!file && !currentItem) {
                        setIsImageValid(false);
                    }
                }}
                defaultValues={rejectedTraffic}
                className="form-in-modal"
            >
                <ModalHeader toggle={closeModal}>
                    {currentItem ? `#${currentItem.id}` : t('BackOfficeNew')}
                </ModalHeader>
                <ModalBody className="custom-scrollbar">
                    <Container className="mb-3 px-0 d-flex justify-content-center">
                        <ImageUpload<
                            AxiosResponse<{ id: number; virtualPath: string }>,
                            AxiosErrorResponse
                        >
                            canUpload={doImageUpload}
                            onUploaded={(res) => {
                                setDoImageUpload(false);
                                if (newData.current) {
                                    newData.current.logoFileId = res.data.id;
                                    saveChanges(newData.current);
                                }
                            }}
                            onError={() => {
                                setDoImageUpload(false);
                                handleError('BackOfficeErrorFileUploadFailed');
                            }}
                            filePath={currentItem?.logoVirtualPath}
                            label={t('BackOfficeLogo')}
                            alt={t('BackOfficeLogoNotFound')}
                            onChange={(f) => setFile(f)}
                            valid={isImageValid}
                            query={async (file) => await uploadFile(api, 'rejectedTraffic', file)}
                        />
                    </Container>
                    <ValidatedInput<RejectedTrafficForm>
                        className="mt-3"
                        type="checkbox"
                        name="isActive"
                        label={t('BackOfficeIsActive')}
                        disabled={!formHasRole()}
                    />
                    <hr />
                    <ValidatedInput<RejectedTrafficForm>
                        type="select"
                        name="leadgenId"
                        disabled={!formHasRole()}
                        label={t('BackOfficeLeadgen')}
                        loading={isFetching}
                        rules={{ required: requiredRule }}
                        options={nameList.map((r, i) => (
                            <option key={i} value={r.id ?? undefined}>
                                {r.name}
                            </option>
                        ))}
                    />
                    <ValidatedInput<RejectedTrafficForm>
                        name="referralLink"
                        disabled={!formHasRole()}
                        label={t('BackOfficeReferralLink')}
                        rules={{ required: requiredRule }}
                    />
                    {isFetchingRules ? <Pending /> : rules}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" type="button" onClick={closeModal}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button
                        type="submit"
                        color="success"
                        disabled={!formHasRole()}
                        isPending={doImageUpload}
                    >
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default RejectedTrafficModal;
